import React, { useEffect, useMemo, useState } from "react";
import { Tooltip } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { componentKey } from "../DashboardSlice";
import { getIcdSummary } from "../DashboardSaga";
import { currentOperationalPeriodWindow } from "../../reports-screen/ReportConstants";

// Window presets for the card. "Operational Period" is the default because it
// is what the IAP runs on (0700-0700 Eastern); the rest are plain calendar
// windows. Each resolves to the UTC ISO instants the API expects.
export const ICD_PERIOD_OPTIONS = [
  { label: "Operational Period", value: "operational" },
  { label: "Today", value: "day" },
  { label: "This Week", value: "week" },
  { label: "Last Week", value: "lastWeek" },
];

export const getIcdPeriodWindow = (period: string) => {
  switch (period) {
    case "day":
      return {
        startDate: moment().startOf("day").toISOString(),
        endDate: moment().toISOString(),
      };
    case "week":
      return {
        startDate: moment().startOf("week").toISOString(),
        endDate: moment().toISOString(),
      };
    case "lastWeek":
      return {
        startDate: moment().subtract(1, "week").startOf("week").toISOString(),
        endDate: moment().subtract(1, "week").endOf("week").toISOString(),
      };
    case "operational":
    default:
      return currentOperationalPeriodWindow();
  }
};

/**
 * Dashboard card: the ICD-10 / DSM codes currently being seen, ranked by how
 * often they were recorded in the selected window, with the facilities that
 * recorded each one.
 *
 * Read-only widget — it owns its own period filter and refetches on change.
 * No facility is sent, so the admin dashboard reports across all facilities
 * (selecting a single facility switches the screen to the facility dashboard).
 */
const IcdCodesCard = ({ refreshKey = 0 }: { refreshKey?: number }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { icdSummary } = useSelector(
    (state: Record<string, any>) => state[componentKey] as Record<string, any>,
  );
  const [period, setPeriod] = useState<string>("operational");

  useEffect(() => {
    const window = getIcdPeriodWindow(period);
    dispatch(
      getIcdSummary({
        startDate: window?.startDate,
        endDate: window?.endDate,
        limit: 5,
      }),
    );
    // refreshKey lets the dashboard's auto-refresh pull the card along.
  }, [period, refreshKey]);

  const codes = useMemo(() => icdSummary?.data ?? [], [icdSummary]);

  return (
    <div className="border-1px-solid-D2D2D2 rounded w-full flex flex-col">
      <div className="flex justify-between items-center m-3 gap-2">
        <Tooltip
          arrow
          placement="top"
          title="Top ICD-10 / DSM codes recorded in the selected period"
        >
          <div className="text-sm font-semibold text-[#565656] whitespace-nowrap cursor-default">
            ICD-10 / DSM Codes
          </div>
        </Tooltip>
        <div className="flex items-center gap-2 min-w-0">
          {/* Native select keeps the header compact — a full MUI dropdown would
              not fit next to the title at a third of the row width. */}
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="text-xs text-[#565656] border border-[#D2D2D2] rounded px-1.5 py-1 outline-none cursor-pointer bg-white"
            aria-label="ICD code period"
          >
            {ICD_PERIOD_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {/* Jump to the full ICD/DSM report (opens the report tab preselected). */}
          <button
            type="button"
            onClick={() => navigate("/reports?tab=icd")}
            className="text-xs font-medium text-[#005596] border border-[#005596] rounded px-2 py-1 whitespace-nowrap cursor-pointer hover:bg-[#F0F9FF]"
          >
            View more
          </button>
        </div>
      </div>

      <div className="px-3 pb-3 flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
        {codes?.length ? (
          codes.map((item: Record<string, any>) => (
            <div
              key={`${item?.code}`}
              className="border border-[#E8E8E8] rounded-md p-2 mb-2"
            >
              <div className="flex justify-between items-center gap-2">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="text-sm font-semibold text-[#393939] whitespace-nowrap">
                    {item?.code}
                  </span>
                  {item?.codeType ? (
                    <span className="text-[10px] text-[#727272] border border-[#E0E0E0] rounded px-1 py-[1px] whitespace-nowrap">
                      {item?.codeType}
                    </span>
                  ) : null}
                </div>
                {/* <Tooltip
                  arrow
                  placement="top"
                  title={`${item?.count} recorded · ${item?.patientCount} patient(s)`}
                >
                  <span className="text-xs text-[#727272] whitespace-nowrap cursor-default">
                    {item?.count}
                  </span>
                </Tooltip> */}
              </div>

              {item?.description ? (
                <Tooltip arrow placement="top" title={item?.description}>
                  <div className="text-xs text-[#727272] truncate mt-0.5 cursor-default">
                    {item?.description}
                  </div>
                </Tooltip>
              ) : null}

              {/* Which facility saw / used this code. */}
              <div className="mt-1.5 flex flex-wrap gap-1">
                {item?.facilities?.length ? (
                  item.facilities.map(
                    (facility: Record<string, any>, index: number) => (
                      <span
                        key={`${item?.code}-${facility?.facilityId ?? index}`}
                        className="text-[11px] text-[#565656] bg-[#F8F8F8] rounded px-1.5 py-[2px] max-w-full truncate"
                        title={`${facility?.facilityName ?? "Unassigned"}: ${facility?.count}`}
                      >
                        {facility?.facilityName ?? "Unassigned"} ·{" "}
                        {facility?.count}
                      </span>
                    ),
                  )
                ) : (
                  <span className="text-[11px] text-[#BCBCBC]">
                    No facility recorded
                  </span>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="text-center text-[#BCBCBC] text-sm py-6">
            No ICD/DSM codes found
          </div>
        )}
      </div>
    </div>
  );
};

export default IcdCodesCard;
